import React, { useState, useEffect } from 'react';
import { isMainList, isTomorrowList } from '../constants/listTypes';
import { api } from '../services/api';
import { checkLocationAvailability } from '../utils/locationUtils';

export default function LocationAwareTasks({ lists }) {
  const [locationTasks, setLocationTasks] = useState([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    items: [''],
    targetListId: '',
    locationName: '',
    cityName: '',
    radius: 100 // meters - how close user needs to be to the city/town
  });

  // Load location-aware tasks from API and check location permission
  useEffect(() => {
    loadLocationTasks();
    checkLocationPermission();
  }, []);

  const checkLocationPermission = async () => {
    const availability = await checkLocationAvailability();
    if (!availability.available && availability.permission === 'denied') {
      setLocationPermissionDenied(true);
    }
  };

  const loadLocationTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const tasks = await api.getLocationTasks();
      setLocationTasks(tasks);
    } catch (err) {
      setError(err.message || 'Failed to load location tasks');
    } finally {
      setLoading(false);
    }
  };

  // Location-aware tasks handlers
  const handleAddLocationItem = () => {
    setLocationFormData({
      ...locationFormData,
      items: [...locationFormData.items, '']
    });
  };

  const handleLocationItemChange = (index, value) => {
    const newItems = [...locationFormData.items];
    newItems[index] = value;
    setLocationFormData({
      ...locationFormData,
      items: newItems
    });
  };

  const handleRemoveLocationItem = (index) => {
    const newItems = locationFormData.items.filter((_, i) => i !== index);
    setLocationFormData({
      ...locationFormData,
      items: newItems.length > 0 ? newItems : ['']
    });
  };

  const handleCreateLocationTask = async (e) => {
    e.preventDefault();
    const validItems = locationFormData.items.filter(item => item.trim());
    
    if (!locationFormData.locationName.trim() || !locationFormData.cityName.trim() || !locationFormData.targetListId || validItems.length === 0) {
      return;
    }

    try {
      setGeocoding(true);
      setError(null);
      
      // Geocode the city name to get coordinates
      const coordinates = await api.geocodeCity(locationFormData.cityName.trim());
      
      // Create the location job
      const newLocationJob = {
        location_name: locationFormData.locationName.trim(),
        city_name: locationFormData.cityName.trim(),
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        radius: locationFormData.radius,
        target_list_id: locationFormData.targetListId,
        tasks: validItems.map(item => ({ text: item.trim() }))
      };

      await api.createLocationTask(newLocationJob);
      
      // Reload location tasks
      await loadLocationTasks();
      
      // Reset form
      setLocationFormData({
        items: [''],
        targetListId: '',
        locationName: '',
        cityName: '',
        radius: 100
      });
      setShowLocationForm(false);
    } catch (err) {
      setError(err.message || 'Failed to create location task');
    } finally {
      setGeocoding(false);
    }
  };

  const handleDeleteLocationTask = async (taskId) => {
    try {
      await api.deleteLocationTask(taskId);
      await loadLocationTasks();
    } catch (err) {
      setError(err.message || 'Failed to delete location task');
    }
  };

  return (
    <>
      {error && (
        <div className="notification notification-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {locationPermissionDenied && (
        <div className="notification" style={{ 
          marginBottom: '1rem', 
          backgroundColor: 'var(--notification-warning-bg)', 
          border: '1px solid var(--notification-warning-border)',
          borderRadius: '4px',
          padding: '0.75rem',
          color: 'var(--notification-warning-text)'
        }}>
          <strong>Location access is disabled</strong>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Location-aware tasks require location access. Please enable location services in your browser settings 
            and allow access for this site. Location tasks will not trigger until location access is enabled.
          </p>
        </div>
      )}
      {!showLocationForm ? (
        <div>
          <button
            className="btn-custom btn-primary-custom"
            onClick={() => setShowLocationForm(true)}
            style={{ marginBottom: '1.5rem' }}
          >
            + Create Location Task
          </button>

          {loading ? (
            <div className="empty-state">
              <div className="empty-state-icon">⏳</div>
              <p className="empty-state-text">Loading location tasks...</p>
            </div>
          ) : locationTasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <p className="empty-state-text">No location-aware tasks yet</p>
              <p className="empty-state-text" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Create one to automatically add tasks when you arrive at a location
              </p>
            </div>
          ) : (
            <div className="location-tasks-list">
              {locationTasks.map(task => (
                <div key={task.id} className="location-task-item">
                  <div className="location-task-header">
                    <div className="location-task-info">
                      <h3 className="location-task-name">
                        <span className="location-icon">📍</span>
                        {task.location_name}
                      </h3>
                      <div className="location-task-meta">
                        <span className="location-task-target">
                          Adds to: <strong>{task.target_list_name || 'Unknown'}</strong>
                        </span>
                        <span className="location-task-items-count">
                          {task.tasks?.length || 0} {(task.tasks?.length || 0) === 1 ? 'item' : 'items'}
                        </span>
                        {task.city_name && (
                          <span className="location-task-city">
                            📍 {task.city_name}
                          </span>
                        )}
                        {task.radius && (
                          <span className="location-task-radius">
                            Radius: {task.radius}m
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="location-task-actions">
                      <button
                        className="btn-delete-list"
                        onClick={() => handleDeleteLocationTask(task.id)}
                        aria-label={`Delete location task for ${task.location_name}`}
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h14M8 6V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2m3 0v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M7 11v6M13 11v6" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="location-task-items">
                    <ul className="location-items-list">
                      {task.tasks?.map((item, idx) => (
                        <li key={item.id || idx} className="location-item-text">
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="location-form-container">
          <div className="form-header">
            <h3 className="form-header-title">Create Location-Aware Task</h3>
            <button
              className="btn-icon"
              onClick={() => {
                setShowLocationForm(false);
                setLocationFormData({
                  items: [''],
                  targetListId: '',
                  locationName: '',
                  cityName: '',
                  radius: 100
                });
              }}
              aria-label="Cancel"
              title="Cancel"
            >
              ×
            </button>
          </div>
          <form onSubmit={handleCreateLocationTask} className="form-body">
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Location Name</label>
              <input
                type="text"
                value={locationFormData.locationName}
                onChange={(e) => setLocationFormData({ ...locationFormData, locationName: e.target.value })}
                placeholder="e.g., Grocery Store, Gym, Office"
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Location name"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                A friendly name for this location task
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">City or Town Name</label>
              <input
                type="text"
                value={locationFormData.cityName}
                onChange={(e) => setLocationFormData({ ...locationFormData, cityName: e.target.value })}
                placeholder="e.g., San Francisco, CA or New York, NY"
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="City or town name"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                The city or town where tasks should be triggered. Include state/country for better accuracy.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Match Radius (meters)</label>
              <input
                type="number"
                min="10"
                max="10000"
                step="1"
                value={locationFormData.radius}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : parseInt(e.target.value);
                  setLocationFormData({ ...locationFormData, radius: value === '' ? 100 : (isNaN(value) ? 100 : value) });
                }}
                className="input-custom"
                style={{ width: '100%' }}
                aria-label="Match radius"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                Tasks will trigger when you're within this distance of the city/town center
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Add Items To</label>
              <select
                value={locationFormData.targetListId}
                onChange={(e) => setLocationFormData({ ...locationFormData, targetListId: e.target.value })}
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Target list"
              >
                <option value="">Select a list...</option>
                {lists.map(list => {
                  const isTomorrow = isTomorrowList(list.list_type);
                  const displayName = isTomorrow ? 'To Do Tomorrow' : list.name;
                  return (
                    <option key={list.id} value={list.id}>
                      {displayName} {isMainList(list.list_type) ? '(Priority)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Items to Add</label>
              {locationFormData.items.map((item, index) => (
                <div key={index} className="location-item-input-group" style={{ marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleLocationItemChange(index, e.target.value)}
                    placeholder="Enter task item..."
                    className="input-custom"
                    style={{ flex: 1, marginRight: '0.5rem' }}
                    aria-label={`Item ${index + 1}`}
                  />
                  {locationFormData.items.length > 1 && (
                    <button
                      type="button"
                      className="btn-icon btn-delete"
                      onClick={() => handleRemoveLocationItem(index)}
                      aria-label={`Remove item ${index + 1}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-custom btn-secondary-custom"
                onClick={handleAddLocationItem}
                style={{ marginTop: '0.5rem' }}
              >
                + Add Another Item
              </button>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-custom btn-primary-custom"
                style={{ flex: 1 }}
                disabled={!locationFormData.locationName.trim() || !locationFormData.targetListId || locationFormData.items.filter(i => i.trim()).length === 0 || geocoding}
              >
                {geocoding ? 'Geocoding...' : 'Create Location Task'}
              </button>
              <button
                type="button"
                className="btn-custom btn-secondary-custom"
                onClick={() => {
                  setShowLocationForm(false);
                  setLocationFormData({
                    items: [''],
                    targetListId: '',
                    locationName: '',
                    cityName: '',
                    radius: 100
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

